import {provideHttpClient} from "@angular/common/http"
import {DoBootstrap, Injectable, Injector, NgModule, provideZonelessChangeDetection, inject} from "@angular/core"
import {createCustomElement} from "@angular/elements"
import {BrowserModule, HammerGestureConfig} from "@angular/platform-browser"
import {provideAnimations} from "@angular/platform-browser/animations"
import {ConfiguratorComponent} from "@app/common/components/viewers/configurator/configurator/configurator.component"
import {BdCloudApiComponent} from "@app/common/components/viewers/configurator/extensions/bd-cloud/bd-cloud-api/bd-cloud-api.component"
import {PdfDownloadButton} from "@app/common/components/viewers/configurator/pdf-download-button/pdf-download-button.component"
import {cmApolloConfig} from "@app/common/config/apollo"
import {WebAssemblyWorkerService} from "@app/template-editor/services/webassembly-worker.service"
import {ConfigMenuComponent} from "@common/components/viewers/configurator/config-menu/config-menu.component"
import {CM_COLORMASS_CLIENT} from "@common/config/client-injector"
import {cmTippyConfig} from "@common/config/tippy"
import {LOGGING_PROVIDERS} from "@common/services/logging"
import {AuthService} from "@common/services/auth/auth.service"
import {InjectorService} from "@common/services/injector/injector.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {UtilsService} from "@legacy/helpers/utils"
import {provideTippyConfig, provideTippyLoader} from "@ngneat/helipopper/config"
import tippy from "tippy.js"
import {provideApollo} from "apollo-angular"
import * as Hammer from "hammerjs"

@Injectable()
export class HammerConfig extends HammerGestureConfig {
    override overrides = {
        swipe: {direction: Hammer.DIRECTION_ALL},
    }
}

@NgModule({
    imports: [BrowserModule],
    providers: [
        {provide: CM_COLORMASS_CLIENT, useValue: "configurator"},
        InjectorService,
        AuthService,
        WebAssemblyWorkerService,
        UtilsService,
        NotificationsService,
        provideZonelessChangeDetection(),
        provideAnimations(),
        provideTippyLoader(() => tippy),
        provideTippyConfig(cmTippyConfig),
        provideHttpClient(),
        provideApollo(cmApolloConfig),
        ...LOGGING_PROVIDERS,
    ],
})
export class ConfiguratorModule implements DoBootstrap {
    private readonly injector = inject(Injector)

    ngDoBootstrap() {
        const ConfiguratorElement = createCustomElement(ConfiguratorComponent, {injector: this.injector})
        customElements.define("cm-configurator-main", ConfiguratorElement)
        const MenuElement = createCustomElement(ConfigMenuComponent, {injector: this.injector})
        //When used standalone as <cm-configurator-menu>, default external-menu="true" so the minimize chevron is hidden.
        //An explicit external-menu attribute on the element still wins. The cast bypasses Angular Elements' abstract
        //NgElement typing — at runtime connectedCallback and the other lifecycle hooks are provided by the strategy.
        const MenuElementCtor = MenuElement as unknown as new (...args: unknown[]) => HTMLElement & {connectedCallback(): void}
        class ExternalConfiguratorMenuElement extends MenuElementCtor {
            override connectedCallback() {
                if (!this.hasAttribute("external-menu")) this.setAttribute("external-menu", "true")
                super.connectedCallback()
            }
        }
        customElements.define("cm-configurator-menu", ExternalConfiguratorMenuElement)
        const PdfDownloadButtonElement = createCustomElement(PdfDownloadButton, {injector: this.injector})
        customElements.define("cm-configurator-pdf-download-button", PdfDownloadButtonElement)
        const BdApi = createCustomElement(BdCloudApiComponent, {injector: this.injector})
        customElements.define("cm-bdcloud-api", BdApi)
    }
}
